/**
 * The licence string.
 *
 *     BP1.<base64url payload>.<base64url signature>
 *
 * Three parts, dot-separated, safe in an environment variable, a YAML file, a
 * URL and a shell argument without quoting. The leading `BP1` is the *string
 * format* version and `payload.version` is the *payload schema* version; they
 * move independently, and both are checked, so a licence issued for a later
 * format is reported as unsupported rather than as corrupt.
 *
 * What is signed is the bytes of `BP1.<payload>` — prefix included. Signing the
 * encoded text rather than the decoded object means verification never depends
 * on two JSON serialisers agreeing about key order or number formatting, and
 * covering the prefix stops a payload being lifted into a future format that
 * might read a field differently.
 */
import { base64UrlToBytes, bytesToBase64Url, bytesToUtf8, utf8ToBytes } from "./encoding.js";
import { PRODUCT, type LicensePayload, type LicenseType, type Plan } from "./types.js";

/** The only string format this SDK can read. */
export const FORMAT_PREFIX = "BP1";

/** A licence string split into its parts, before any signature check. */
export interface ParsedLicense {
  prefix: string;
  /** The exact text that was signed. */
  signedText: string;
  encodedPayload: string;
  signature: Uint8Array;
  payload: LicensePayload;
}

export class LicenseFormatError extends Error {
  constructor(
    message: string,
    /** `unsupported` when the licence is well-formed but newer than this SDK. */
    readonly kind: "invalid" | "unsupported" = "invalid"
  ) {
    super(message);
    this.name = "LicenseFormatError";
  }
}

const PLANS: readonly Plan[] = ["internal", "saas", "evaluation"];

/**
 * The licence types this SDK can actually honour.
 *
 * Only `subscription`. A perpetual licence's whole meaning is that the right to
 * use outlives the right to updates, which needs the running build's release
 * date to enforce — a fact a certificate cannot carry, because it is issued
 * before the versions it covers exist. A build with no such date could only
 * honour a perpetual licence by honouring *every* future version forever, which
 * is not what was sold. So it is refused, and refused as `unsupported` rather
 * than `invalid`: the fix is to upgrade, and saying so is more use than calling
 * a valid certificate corrupt.
 */
const LICENSE_TYPES: readonly LicenseType[] = ["subscription"];

/** Whitespace is stripped: licences get pasted, wrapped and indented. */
export function normalizeLicense(license: string): string {
  return license.replace(/\s+/g, "");
}

/**
 * Reads a licence string without verifying it.
 *
 * Nothing here decides whether a licence is *honoured* — that needs the
 * signature — but a structurally broken string can be rejected before any
 * cryptography happens, and the account UI can display a licence's contents
 * without holding a public key.
 */
export function parseLicense(license: string): ParsedLicense {
  const text = normalizeLicense(license);
  if (!text) throw new LicenseFormatError("the licence string is empty");

  const parts = text.split(".");
  if (parts.length !== 3) throw new LicenseFormatError(`expected three dot-separated parts, found ${parts.length}`);
  const [prefix, encodedPayload, encodedSignature] = parts as [string, string, string];

  if (prefix !== FORMAT_PREFIX) {
    // A licence issued in a format this SDK predates is a version problem, not
    // a corrupt string, and the fix is to upgrade rather than to re-copy it.
    const known = /^BP\d+$/.test(prefix);
    throw new LicenseFormatError(
      known
        ? `licence format ${prefix} is newer than this version of BroadPaper, which reads ${FORMAT_PREFIX}`
        : `unrecognised licence prefix ${JSON.stringify(prefix)}`,
      known ? "unsupported" : "invalid"
    );
  }
  if (!encodedPayload || !encodedSignature) throw new LicenseFormatError("the licence string has an empty part");

  let json: string;
  let signature: Uint8Array;
  try {
    json = bytesToUtf8(base64UrlToBytes(encodedPayload));
    signature = base64UrlToBytes(encodedSignature);
  } catch (e) {
    throw new LicenseFormatError(`the licence string is not valid base64 (${(e as Error).message})`);
  }
  if (signature.length !== 64) throw new LicenseFormatError(`an Ed25519 signature is 64 bytes, this one is ${signature.length}`);

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new LicenseFormatError("the licence payload is not valid JSON");
  }

  return {
    prefix,
    signedText: `${prefix}.${encodedPayload}`,
    encodedPayload,
    signature,
    payload: readPayload(raw)
  };
}

/**
 * Validates the payload's shape.
 *
 * Unknown properties are kept, not rejected: they are inside the signature, so
 * a licence issued by a later BroadPaper carrying a field this SDK has never
 * heard of is still a licence this SDK can honour.
 */
function readPayload(raw: unknown): LicensePayload {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) throw new LicenseFormatError("the licence payload is not an object");
  const o = raw as Record<string, unknown>;

  const schemaVersion = o.schemaVersion;
  if (schemaVersion !== 1) {
    const numeric = typeof schemaVersion === "number" && Number.isInteger(schemaVersion) && schemaVersion > 1;
    throw new LicenseFormatError(
      numeric
        ? `licence payload schema version ${schemaVersion} is newer than this version of BroadPaper, which reads version 1`
        : "the licence payload has no usable schemaVersion",
      numeric ? "unsupported" : "invalid"
    );
  }

  const text = (key: string): string => {
    const value = o[key];
    if (typeof value !== "string" || value === "") throw new LicenseFormatError(`the licence payload is missing ${key}`);
    return value;
  };
  const seconds = (key: string): number => {
    const value = o[key];
    if (typeof value !== "number" || !Number.isFinite(value)) throw new LicenseFormatError(`the licence payload is missing ${key}`);
    return value;
  };
  // `null` is the honest encoding of "no end date" — a sentinel year 9999 would
  // be a date somebody eventually compares against. Absent is still an error;
  // only an explicit null means never.
  const secondsOrNever = (key: string): number | null => {
    const value = o[key];
    if (value === null) return null;
    return seconds(key);
  };

  const plan = text("plan");
  if (!PLANS.includes(plan as Plan)) {
    // A plan this SDK does not know is a newer commercial model, and refusing
    // to honour a licence we cannot classify would punish the customer for
    // buying something new. It is reported as unsupported so the developer is
    // told to upgrade.
    throw new LicenseFormatError(`this version of BroadPaper does not know the plan ${JSON.stringify(plan)}`, "unsupported");
  }

  // An evaluation licence is a term by definition. One with no end date could
  // only be a mistake, and honouring it would be a free licence forever.
  if (plan === "evaluation" && o.validUntil === null) {
    throw new LicenseFormatError("an evaluation licence must have an end date");
  }

  const licenseType = text("licenseType");
  if (!LICENSE_TYPES.includes(licenseType as LicenseType)) {
    throw new LicenseFormatError(`this version of BroadPaper cannot honour a ${JSON.stringify(licenseType)} licence`, "unsupported");
  }

  return {
    ...o,
    schemaVersion: 1,
    keyId: text("keyId"),
    licenseId: text("licenseId"),
    organisationId: text("organisationId"),
    organisationName: text("organisationName"),
    product: text("product") as typeof PRODUCT,
    plan: plan as Plan,
    licenseType: licenseType as LicenseType,
    issuedAt: seconds("issuedAt"),
    validUntil: secondsOrNever("validUntil"),
    updatesUntil: seconds("updatesUntil")
  };
}

/**
 * Encodes a payload into the text that gets signed.
 *
 * Exported because BroadPaper's own licence issuer must produce byte-identical
 * input to what the SDK verifies, and the only way to guarantee that is for
 * both to call this function.
 */
export function encodeSigningInput(payload: LicensePayload): { signedText: string; encodedPayload: string } {
  const encodedPayload = bytesToBase64Url(utf8ToBytes(JSON.stringify(payload)));
  return { encodedPayload, signedText: `${FORMAT_PREFIX}.${encodedPayload}` };
}

/** Assembles the final licence string from a payload and its signature. */
export function formatLicense(payload: LicensePayload, signature: Uint8Array): string {
  if (signature.length !== 64) throw new LicenseFormatError(`an Ed25519 signature is 64 bytes, this one is ${signature.length}`);
  const { encodedPayload } = encodeSigningInput(payload);
  return `${FORMAT_PREFIX}.${encodedPayload}.${bytesToBase64Url(signature)}`;
}
